import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    Spinner,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@elit-hair/elit-ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { useUserStatusContext } from "@/providers/general/user-status.provider";
import { useCall } from "@/providers/hooks/use-call";
import { SELECTABLE_USER_STATUSES, UserStatus } from "@/types/user-statuses.types";
import { getStatusBorderColor, getStatusCircleColor, getStatusIcon, getStatusTextColor } from "@/utils/user-status";

const HOLLOW_STATUSES = new Set([UserStatus.OFFLINE, UserStatus.SILENT, UserStatus.INACTIVE]);

function StatusDot({ status }: { status: UserStatus }) {
    const isHollow = HOLLOW_STATUSES.has(status);

    return (
        <span
            className={cn(
                "inline-block h-2.5 w-2.5 shrink-0 rounded-full",
                isHollow ? cn("border-2 bg-transparent", getStatusBorderColor(status)) : getStatusCircleColor(status),
                !isHollow && "animate-pulse"
            )}
        />
    );
}

export function HeaderStatusDropdown() {
    const { t } = useTranslation();
    const { status, isLoading, updateStatus, isConnected } = useUserStatusContext();
    const { isCallActive } = useCall();
    const [open, setOpen] = useState<boolean>(false);

    const tooltipLabel = isLoading ? t("loading") : status ? t(`user_status.${status}`) : t("select_your_status");

    const handleOpenChange = (newOpen: boolean) => {
        if (newOpen && (status === UserStatus.IN_CALL || isCallActive())) {
            return;
        }
        setOpen(newOpen);
    };

    const handleValueChange = (value: UserStatus) => {
        if (status === UserStatus.IN_CALL || isCallActive()) {
            return;
        }

        updateStatus(value);
        setOpen(false);
    };

    return (
        <Select
            onValueChange={handleValueChange}
            value={status ?? UserStatus.OFFLINE}
            open={open}
            disabled={!isConnected}
            onOpenChange={handleOpenChange}
        >
            <Tooltip>
                <TooltipTrigger asChild>
                    <SelectTrigger
                        className={cn(
                            "h-8 w-8 shrink-0 border-0 bg-transparent p-0 shadow-none",
                            "justify-center hover:bg-muted focus:ring-0",
                            "[&>svg:last-child]:hidden"
                        )}
                        disabled={isLoading || !isConnected}
                        aria-label={tooltipLabel}
                    >
                        {isLoading ? (
                            <Spinner size="sm" />
                        ) : status ? (
                            <StatusDot status={status} />
                        ) : (
                            <span className="text-xs text-muted-foreground">?</span>
                        )}
                    </SelectTrigger>
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={5}>
                    {tooltipLabel}
                </TooltipContent>
            </Tooltip>
            <SelectContent align="end">
                {SELECTABLE_USER_STATUSES.map((value) => (
                    <SelectItem key={value} value={value} className="cursor-pointer hover:bg-muted transition-colors">
                        <p className="flex items-center gap-2">
                            <span className={cn("text-sm", getStatusTextColor(value))}>
                                {value === UserStatus.SILENT ? (
                                    getStatusIcon(value)
                                ) : (
                                    <span className="text-sm animate-pulse">{getStatusIcon(value)}</span>
                                )}
                            </span>
                            <span className={cn("text-sm font-bold", getStatusTextColor(value))}>
                                {t(`user_status.${value}`)}
                            </span>
                        </p>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}
